import { ExportFileResult } from "../models/dataModels";
import { ApiFetcher } from "../utils/ApiFetcher";

/*
 * Los tipos de este fichero son el reflejo de SHub.Messages/LogsProveedores. Están declarados aquí y no
 * importados de models/dataModels porque ese fichero lo genera NSwag contra el swagger de SHub.WebApi
 * (nswag run WebApi.nswag, con la API levantada) y aún no se ha regenerado. Cuando se regenere, estas
 * interfaces se pueden borrar y pasar a importarse de dataModels como el resto.
 */

/** Qué lado de la conversación con la editorial documenta un fichero de log. */
export enum TipoLogProveedor {
    Desconocido = "Desconocido",
    Peticion = "Peticion",
    Respuesta = "Respuesta"
}

/** Una subcarpeta del árbol de logs (editorial, año, mes o día). */
export interface LogCarpetaListItem {
    nombre: string;
    ruta: string;
    fechaModificacion: Date;
}

/** Un fichero de log: la petición enviada a la editorial o su respuesta. */
export interface LogFicheroListItem {
    nombre: string;
    ruta: string;
    tamanno: number;
    fechaModificacion: Date;
    correlationId: string | null;
    tipo: TipoLogProveedor;
}

/** Contenido de una carpeta del árbol de logs. */
export interface LogsProveedoresListado {
    ruta: string;
    rutaPadre: string | null;
    carpetas: LogCarpetaListItem[];
    ficheros: LogFicheroListItem[];
    totalFicheros: number;
    hayMasFicheros: boolean;
    aviso: string | null;
}

/** Contenido de un fichero de log para el visor. */
export interface LogProveedorContenido {
    ruta: string;
    nombre: string;
    tamanno: number;
    contenido: string;
    recortado: boolean;
}

/** Petición de purga de logs antiguos. */
export interface LogsProveedoresPurgarMessage {
    /** Se borran los ficheros escritos antes de esta fecha. */
    anterioresA: string;
    /** Carpeta a purgar; vacío para todo el árbol. */
    ruta: string;
    /** true para contar sin borrar. */
    simular: boolean;
}

/** Resultado de una purga, real o simulada. */
export interface LogsProveedoresPurgaResultado {
    simulado: boolean;
    ficherosEliminados: number;
    bytesLiberados: number;
    carpetasEliminadas: number;
    errores: string[];
    aviso: string | null;
}

/** Resultado del borrado de una carpeta completa, real o simulado. */
export interface LogCarpetaEliminarResultado {
    ruta: string;
    /** true si solo se ha contado, sin borrar. */
    simulado: boolean;
    ficherosEliminados: number;
    bytesLiberados: number;
    /** Cuenta la propia carpeta además de sus descendientes. */
    carpetasEliminadas: number;
    aviso: string | null;
}

/**
 * Cliente de la API de logs de proveedores educativos. Todas sus llamadas exigen ser AdminGlobal: el
 * backend lo comprueba con [AuthorizationFilter(TipoUsuario.AdminGlobal)].
 */
export class LogsProveedoresService {

    private apiFetcher: ApiFetcher;

    constructor() {
        this.apiFetcher = new ApiFetcher();
    }

    /** Contenido de una carpeta; ruta vacía para la raíz. */
    public listar(ruta: string): Promise<LogsProveedoresListado> {
        return this.apiFetcher
            .getByUrl<LogsProveedoresListado>(`intranet/logsProveedores/listar?ruta=${encodeURIComponent(ruta || "")}`)
            .then(listado => {
                // NSwag declara las fechas como Date pero axios las entrega como string: se rehidratan aquí,
                // igual que en MatriculasService.
                (listado.carpetas || []).forEach(c => c.fechaModificacion = new Date(c.fechaModificacion));
                (listado.ficheros || []).forEach(f => f.fechaModificacion = new Date(f.fechaModificacion));
                return listado;
            });
    }

    /** Texto de un fichero para verlo en pantalla. */
    public ver(ruta: string): Promise<LogProveedorContenido> {
        return this.apiFetcher.getByUrl<LogProveedorContenido>(
            `intranet/logsProveedores/ver?ruta=${encodeURIComponent(ruta)}`);
    }

    /** Fichero completo para descargarlo. */
    public descargar(ruta: string): Promise<ExportFileResult> {
        return this.apiFetcher.getByUrl<ExportFileResult>(
            `intranet/logsProveedores/descargar?ruta=${encodeURIComponent(ruta)}`);
    }

    /**
     * Borra los logs anteriores a una fecha, o cuenta cuántos se borrarían.
     *
     * @param anterioresA Fecha a partir de la cual se conservan los logs (yyyy-MM-dd).
     * @param ruta Carpeta a purgar; vacío para todo el árbol.
     * @param simular true para contar sin borrar nada.
     */
    public purgar(anterioresA: string, ruta: string, simular: boolean): Promise<LogsProveedoresPurgaResultado> {
        return this.apiFetcher.post<LogsProveedoresPurgarMessage, LogsProveedoresPurgaResultado>(
            `intranet/logsProveedores/purgar`,
            { anterioresA: anterioresA, ruta: ruta || "", simular: simular });
    }

    /**
     * Borra una carpeta del árbol de logs con todo su contenido, de una sola vez, o cuenta lo que hay
     * dentro sin borrarlo. Es el camino rápido cuando lo que se quiere es tirar una editorial, un año o un
     * mes enteros; la raíz no se puede borrar.
     *
     * @param ruta Carpeta a borrar, relativa a la raíz de logs.
     * @param simular true para contar sin borrar nada, que es lo que se hace al pedir confirmación.
     */
    public eliminarCarpeta(ruta: string, simular: boolean): Promise<LogCarpetaEliminarResultado> {
        return this.apiFetcher.post<{ ruta: string, simular: boolean }, LogCarpetaEliminarResultado>(
            `intranet/logsProveedores/eliminarCarpeta`,
            { ruta: ruta, simular: simular });
    }
}
